/**
 * Copy text to clipboard with fallback for restricted contexts (e.g., iframes)
 * 
 * This utility attempts to use the modern Clipboard API first, and falls back
 * to the legacy execCommand approach when the Clipboard API is blocked by
 * permissions policies (common in Office Add-in iframes).
 * 
 * @param text - The text to copy to clipboard
 * @returns Promise that resolves when copy is successful
 * @throws Error if both methods fail
 */
export async function copyToClipboard(text: string): Promise<void> {
  // First, try the modern Clipboard API
  try {
    await navigator.clipboard.writeText(text);
    return;
  } catch (clipboardError) {
    console.warn('Clipboard API failed, attempting fallback method:', clipboardError);
    
    // Fallback to execCommand method for iframe contexts
    return fallbackCopyToClipboard(text);
  }
}

/**
 * Fallback copy method using document.execCommand
 * Works in contexts where Clipboard API is blocked
 */
function fallbackCopyToClipboard(text: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // Create a temporary textarea element
    const textarea = document.createElement('textarea');
    
    // Style it to be invisible but still selectable
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '-9999px';
    textarea.style.opacity = '0';
    textarea.setAttribute('readonly', '');
    
    // Add to DOM
    document.body.appendChild(textarea);
    
    try {
      // Select the text
      textarea.select();
      textarea.setSelectionRange(0, text.length);
      
      // Execute copy command
      const successful = document.execCommand('copy');
      
      if (successful) {
        resolve();
      } else {
        reject(new Error('execCommand copy failed'));
      }
    } catch (error) {
      reject(error);
    } finally {
      // Clean up
      document.body.removeChild(textarea);
    }
  });
}
